import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type InputHTMLAttributes,
  type HTMLAttributes,
} from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMagnifyingGlass } from '@fortawesome/pro-solid-svg-icons';
import { cn } from '../../../lib/utils';

/* ─── Context ─── */

type CommandContextValue = {
  search: string;
  setSearch: (value: string) => void;
  selectedValue: string;
  setSelectedValue: (value: string) => void;
  registerItem: (value: string) => void;
  unregisterItem: (value: string) => void;
  getVisibleItems: () => string[];
};

const CommandContext = createContext<CommandContextValue | null>(null);

function useCommandContext() {
  const ctx = useContext(CommandContext);
  if (!ctx)
    throw new Error('Command components must be used within CharlieCommand');
  return ctx;
}

/* ─── Root ─── */

export type CharlieCommandProps = HTMLAttributes<HTMLDivElement>;

export function CharlieCommand({
  className,
  children,
  ...props
}: CharlieCommandProps) {
  const [search, setSearch] = useState('');
  const [selectedValue, setSelectedValue] = useState('');
  const itemsRef = useRef<Set<string>>(new Set());
  const rootRef = useRef<HTMLDivElement>(null);

  const registerItem = useCallback((value: string) => {
    itemsRef.current.add(value);
  }, []);

  const unregisterItem = useCallback((value: string) => {
    itemsRef.current.delete(value);
  }, []);

  const getVisibleItems = useCallback((): string[] => {
    if (!rootRef.current) return [];
    const items = rootRef.current.querySelectorAll<HTMLElement>(
      '[data-slot="command-item"]',
    );
    return Array.from(items)
      .filter(
        (el) =>
          el.style.display !== 'none' &&
          el.getAttribute('data-disabled') !== 'true',
      )
      .map((el) => el.getAttribute('data-value') ?? '');
  }, []);

  const ctx = useMemo(
    () => ({
      search,
      setSearch,
      selectedValue,
      setSelectedValue,
      registerItem,
      unregisterItem,
      getVisibleItems,
    }),
    [search, selectedValue, registerItem, unregisterItem, getVisibleItems],
  );

  // Select first visible item when search changes
  useEffect(() => {
    // Defer so items have time to update visibility
    const id = requestAnimationFrame(() => {
      const visible = getVisibleItems();
      if (visible.length > 0 && !visible.includes(selectedValue)) {
        setSelectedValue(visible[0]);
      }
    });
    return () => cancelAnimationFrame(id);
  }, [search, getVisibleItems, selectedValue]);

  // Keyboard navigation
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      const visible = getVisibleItems();
      if (visible.length === 0) return;

      const currentIndex = visible.indexOf(selectedValue);

      if (e.key === 'ArrowDown') {
        e.preventDefault();
        const next = currentIndex < visible.length - 1 ? currentIndex + 1 : 0;
        setSelectedValue(visible[next]);
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        const prev = currentIndex > 0 ? currentIndex - 1 : visible.length - 1;
        setSelectedValue(visible[prev]);
      } else if (e.key === 'Enter') {
        e.preventDefault();
        if (selectedValue && rootRef.current) {
          const selected = rootRef.current.querySelector<HTMLElement>(
            `[data-slot="command-item"][data-value="${CSS.escape(selectedValue)}"]`,
          );
          selected?.click();
        }
      }
    },
    [getVisibleItems, selectedValue],
  );

  return (
    <CommandContext.Provider value={ctx}>
      <div
        ref={rootRef}
        data-slot="command"
        className={cn(
          'flex h-full w-full flex-col overflow-hidden rounded-md bg-popover text-popover-foreground',
          className,
        )}
        onKeyDown={handleKeyDown}
        {...props}
      >
        {children}
      </div>
    </CommandContext.Provider>
  );
}

/* ─── Input ─── */

export type CharlieCommandInputProps = InputHTMLAttributes<HTMLInputElement>;

export function CharlieCommandInput({
  className,
  ...props
}: CharlieCommandInputProps) {
  const { search, setSearch } = useCommandContext();

  return (
    <div
      className="flex items-center gap-2 border-b px-3"
      data-slot="command-input-wrapper"
    >
      <FontAwesomeIcon
        icon={faMagnifyingGlass}
        className="size-4 shrink-0 opacity-50"
      />
      <input
        data-slot="command-input"
        className={cn(
          'flex h-10 w-full rounded-md bg-transparent py-3 text-sm outline-none',
          'placeholder:text-muted-foreground',
          'disabled:cursor-not-allowed disabled:opacity-50',
          className,
        )}
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        autoFocus
        autoComplete="off"
        {...props}
      />
    </div>
  );
}

/* ─── List ─── */

export type CharlieCommandListProps = HTMLAttributes<HTMLDivElement>;

export function CharlieCommandList({
  className,
  ...props
}: CharlieCommandListProps) {
  return (
    <div
      data-slot="command-list"
      role="listbox"
      className={cn(
        'max-h-[300px] overflow-y-auto overflow-x-hidden',
        className,
      )}
      {...props}
    />
  );
}

/* ─── Empty ─── */

export type CharlieCommandEmptyProps = HTMLAttributes<HTMLDivElement>;

export function CharlieCommandEmpty({
  children,
  ...props
}: CharlieCommandEmptyProps) {
  const { search, getVisibleItems } = useCommandContext();
  const [visible, setVisible] = useState(true);

  useEffect(() => {
    const id = requestAnimationFrame(() => {
      setVisible(getVisibleItems().length === 0 && search.length > 0);
    });
    return () => cancelAnimationFrame(id);
  }, [search, getVisibleItems]);

  if (!visible) return null;

  return (
    <div
      data-slot="command-empty"
      className="py-6 text-center text-sm"
      {...props}
    >
      {children}
    </div>
  );
}

/* ─── Group ─── */

export type CharlieCommandGroupProps = HTMLAttributes<HTMLDivElement> & {
  heading?: React.ReactNode;
};

export function CharlieCommandGroup({
  className,
  heading,
  children,
  ...props
}: CharlieCommandGroupProps) {
  return (
    <div
      data-slot="command-group"
      role="group"
      className={cn(
        'overflow-hidden p-1 text-foreground',
        '[&_[data-slot=command-group-heading]]:px-2 [&_[data-slot=command-group-heading]]:py-1.5 [&_[data-slot=command-group-heading]]:text-xs [&_[data-slot=command-group-heading]]:font-medium [&_[data-slot=command-group-heading]]:text-muted-foreground',
        className,
      )}
      {...props}
    >
      {heading && <div data-slot="command-group-heading">{heading}</div>}
      {children}
    </div>
  );
}

/* ─── Item ─── */

export type CharlieCommandItemProps = HTMLAttributes<HTMLDivElement> & {
  value?: string;
  disabled?: boolean;
  onSelect?: (value: string) => void;
};

export function CharlieCommandItem({
  className,
  value = '',
  disabled,
  onSelect,
  children,
  ...props
}: CharlieCommandItemProps) {
  const {
    search,
    selectedValue,
    setSelectedValue,
    registerItem,
    unregisterItem,
  } = useCommandContext();

  const isSelected = selectedValue === value;
  const isVisible =
    !search || value.toLowerCase().includes(search.toLowerCase());

  useEffect(() => {
    registerItem(value);
    return () => unregisterItem(value);
  }, [value, registerItem, unregisterItem]);

  if (!isVisible) return null;

  return (
    <div
      data-slot="command-item"
      data-value={value}
      data-selected={isSelected}
      data-disabled={disabled || undefined}
      role="option"
      aria-selected={isSelected}
      className={cn(
        'relative flex cursor-default select-none items-center gap-2 rounded-sm px-2 py-2.5 text-sm outline-none',
        'data-[selected=true]:bg-accent data-[selected=true]:text-accent-foreground',
        'data-[disabled=true]:pointer-events-none data-[disabled=true]:opacity-50',
        '[&_svg]:pointer-events-none [&_svg]:shrink-0',
        className,
      )}
      onMouseMove={() => {
        if (!disabled && selectedValue !== value) setSelectedValue(value);
      }}
      onClick={() => {
        if (!disabled && onSelect) onSelect(value);
      }}
      {...props}
    >
      {children}
    </div>
  );
}

/* ─── Separator ─── */

export type CharlieCommandSeparatorProps = HTMLAttributes<HTMLDivElement>;

export function CharlieCommandSeparator({
  className,
  ...props
}: CharlieCommandSeparatorProps) {
  return (
    <div
      data-slot="command-separator"
      role="separator"
      className={cn('-mx-1 h-px bg-border', className)}
      {...props}
    />
  );
}
